import { Badge, Box, Flex, ScrollArea, Tabs, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { Dialog } from "@/components/shared/dialog";
import { RegressionTestRow } from "./types";
import { formatDuration } from "./format";

interface TestDetailDialogProps {
  open: boolean;
  onClose: () => void;
  test: RegressionTestRow;
}

export function TestDetailDialog({
  open,
  onClose,
  test,
}: TestDetailDialogProps) {
  const userInfoEntries = useMemo(() => {
    if (!test.userInfo) return [];
    return Object.entries(test.userInfo).filter(
      ([, v]) => v !== "" && v !== null && v !== undefined
    );
  }, [test.userInfo]);

  const score = test.overallScore ?? 0;
  const scoreColor = score >= 100 ? "green" : score >= 75 ? "amber" : "red";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Content size="large">
        <Flex
          direction="column"
          gap="4"
          p="6"
          style={{ maxHeight: "85vh" }}
        >
          <Flex justify="between" align="start" gap="4">
            <Flex direction="column" gap="2" style={{ flex: 1, minWidth: 0 }}>
              <Dialog.Title>{test.testName}</Dialog.Title>
              <Flex gap="2" wrap="wrap" align="center">
                <Badge color={scoreColor} variant="solid" size="2">
                  {score}%
                </Badge>
                <Badge color="gray" variant="soft" size="2">
                  Pass rate: {test.passRate ?? "-"}
                </Badge>
                <Badge color="gray" variant="soft" size="2">
                  {formatDuration(test.duration ?? 0)}
                </Badge>
                {test.escalated ? (
                  <Badge
                    color={test.escalatedDueToError ? "red" : "amber"}
                    variant="soft"
                    size="2"
                  >
                    Escalated
                    {test.escalatedAtTurn != null
                      ? ` @ turn ${test.escalatedAtTurn}`
                      : ""}
                  </Badge>
                ) : null}
                <Text size="1" color="gray" className="font-mono">
                  {test.taskId}
                </Text>
              </Flex>
            </Flex>
            <Dialog.Close />
          </Flex>

          <Tabs.Root defaultValue="conversation">
            <Tabs.List>
              <Tabs.Trigger value="conversation">Conversation</Tabs.Trigger>
              <Tabs.Trigger value="evaluation">Evaluation</Tabs.Trigger>
              <Tabs.Trigger value="userInfo">User info</Tabs.Trigger>
            </Tabs.List>

            <ScrollArea style={{ maxHeight: "65vh" }}>
              <Box pt="4">
                <Tabs.Content value="conversation">
                  <ConversationView test={test} />
                </Tabs.Content>
                <Tabs.Content value="evaluation">
                  <EvaluationView test={test} />
                </Tabs.Content>
                <Tabs.Content value="userInfo">
                  <Flex direction="column" gap="2">
                    {userInfoEntries.length === 0 ? (
                      <Text size="2" color="gray">
                        No user info
                      </Text>
                    ) : (
                      userInfoEntries.map(([k, v]) => (
                        <Flex key={k} gap="3">
                          <Text
                            size="1"
                            color="gray"
                            style={{ minWidth: 200, flexShrink: 0 }}
                          >
                            {k}
                          </Text>
                          <Text size="1" style={{ wordBreak: "break-all" }}>
                            {String(v)}
                          </Text>
                        </Flex>
                      ))
                    )}
                  </Flex>
                </Tabs.Content>
              </Box>
            </ScrollArea>
          </Tabs.Root>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}

function ConversationView({ test }: { test: RegressionTestRow }) {
  const turns = test.conversation ?? [];
  if (turns.length === 0) {
    return (
      <Text size="2" color="gray">
        No conversation recorded
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="3">
      {turns.map((turn, idx) => {
        const isUser = turn.role === "user";
        return (
          <Flex
            key={idx}
            justify={isUser ? "end" : "start"}
            style={{ width: "100%" }}
          >
            <Box
              style={{
                maxWidth: "80%",
                background: isUser ? "var(--accent-4)" : "var(--gray-3)",
                padding: "10px 14px",
                borderRadius: 12,
              }}
            >
              <Flex direction="column" gap="1">
                <Flex gap="2" align="center">
                  <Text size="1" weight="bold" color={isUser ? undefined : "gray"}>
                    {isUser ? "User" : "Agent"}
                  </Text>
                  {turn.escalated ? (
                    <Badge color="amber" variant="soft" size="1">
                      escalated
                    </Badge>
                  ) : null}
                  {turn.timestamp ? (
                    <Text size="1" color="gray">
                      {new Date(turn.timestamp).toLocaleTimeString()}
                    </Text>
                  ) : null}
                </Flex>
                <Text size="2" style={{ whiteSpace: "pre-wrap" }}>
                  {turn.content}
                </Text>
              </Flex>
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}

function EvaluationView({ test }: { test: RegressionTestRow }) {
  const results = test.evaluationResults ?? [];
  if (results.length === 0) {
    return (
      <Text size="2" color="gray">
        No evaluation results
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="3">
      {results.map((r, idx) => {
        const passed =
          r.pass ?? r.passed ?? (r.score != null && Number(r.score) >= 1);
        return (
          <Box
            key={idx}
            style={{
              border: "1px solid var(--gray-5)",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Flex direction="column" gap="2">
              <Flex gap="2" align="center" justify="between">
                <Text size="2" weight="medium">
                  {String(r.criterion ?? `Criterion ${idx + 1}`)}
                </Text>
                <Flex gap="2" align="center">
                  {r.category ? (
                    <Badge color="gray" variant="soft" size="1">
                      {String(r.category)}
                    </Badge>
                  ) : null}
                  <Badge
                    color={passed ? "green" : "red"}
                    variant="soft"
                    size="1"
                  >
                    {passed ? "Pass" : "Fail"}
                  </Badge>
                </Flex>
              </Flex>
              {r.reasoning ? (
                <Text size="1" color="gray" style={{ whiteSpace: "pre-wrap" }}>
                  {String(r.reasoning)}
                </Text>
              ) : null}
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
}
